import React, { useState } from 'react';
import { useTenantConfig } from '../../hooks/useTenant';
import { F, C, btn, inp, lbl } from '../../config/styles';
import { uid } from '../../utils/products';
import { importCSVs } from '../../utils/csv';
import { rehostRemoteImages } from '../../utils/imageImport';

// ─────────────────────────────────────────────────────────────
// IMPORT MODAL
// ─────────────────────────────────────────────────────────────

export function ImportModal({ onImport, onClose, existingProducts = [] }) {
  const tenantConfig = useTenantConfig();
  const [csvTexts, setCsvTexts] = useState([]);
  const [fileNames, setFileNames] = useState([]);
  const [preview, setPreview] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [imgProg, setImgProg] = useState(null); // { current, total } while re-hosting
  const [imgErrors, setImgErrors] = useState([]);
  const [imgBlocked, setImgBlocked] = useState(false);

  // A ceiling on one import's downloads. Every image costs transfer now and Supabase
  // storage indefinitely, so a sheet this large is worth a second look before it runs —
  // it's usually a whole catalogue export rather than the season being worked on.
  const MAX_IMAGES_PER_IMPORT = 2000;

  const remoteImageCount = (preview || []).reduce(
    (n, p) => n + (p.images || []).filter(i => i?.remote).length, 0);

  // Image columns give us links on someone else's CDN. Download them into this workspace
  // before the products are saved, so an expiring or hotlink-protected link can't break
  // them later. A link that fails is kept as-is rather than dropping the whole import.
  const confirmImport = async () => {
    // Nothing to fetch — or they've been shown why we stopped (too many, or some failed)
    // and pressed again, which means "go ahead without them". Those links stay on the
    // products as they are rather than being retried indefinitely.
    if (!remoteImageCount || imgErrors.length || imgBlocked) { onImport(preview); onClose(); return; }
    if (remoteImageCount > MAX_IMAGES_PER_IMPORT) { setImgBlocked(true); return; }
    setImgProg({ current: 0, total: remoteImageCount });
    setImgErrors([]);
    const { products, errors } = await rehostRemoteImages(preview, {
      scope: tenantConfig?.tenantId,
      onProgress: (current, total) => setImgProg({ current, total }),
    });
    setImgProg(null);
    if (errors.length) {
      setImgErrors(errors);
      setPreview(products); // let them see what failed and re-run, or import anyway
      return;
    }
    onImport(products);
    onClose();
  };

  const handleFiles = (fileList) => {
    const files = Array.from(fileList);
    let loaded = [];
    let names = [];
    let done = 0;
    files.forEach((file, i) => {
      const r = new FileReader();
      r.onload = e => {
        loaded[i] = e.target.result;
        names[i] = file.name;
        if (++done === files.length) {
          setCsvTexts(loaded);
          setFileNames(names);
        }
      };
      r.readAsText(file);
    });
  };

  const doPreview = () => {
    setParsing(true);
    setTimeout(() => {
      try { setPreview(importCSVs(csvTexts, existingProducts, tenantConfig)); }
      catch (e) { alert('Parse error: ' + e.message); }
      finally { setParsing(false); }
    }, 50);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: C.white, borderRadius: 12, padding: 24, width: 540, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ fontFamily: F.display, fontSize: 22, color: C.text }}>Import CSV</div>
          <button style={btn('ghost', { fontSize: 20, padding: '2px 8px', color: C.muted })} onClick={onClose}>×</button>
        </div>

        {!preview ? (
          <>
            <div style={{ marginBottom: 20 }}>
              <p style={{ fontSize: 13, color: C.muted, marginBottom: 12 }}>
                Select one or more CSV files: product build, metafields, fit &amp; fabric, care instructions. All columns are detected automatically.
              </p>
              <label style={{ display: 'block', padding: 20, background: C.fa, border: `2px dashed ${csvTexts.length ? '#6ee7b7' : C.e8}`, borderRadius: 10, textAlign: 'center', cursor: 'pointer' }}>
                <div style={{ fontSize: 13, color: csvTexts.length ? '#065f46' : C.muted, marginBottom: 6 }}>
                  {csvTexts.length ? `${csvTexts.length} file${csvTexts.length > 1 ? 's' : ''} loaded` : 'Click to select CSV files'}
                </div>
                {fileNames.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 8 }}>
                    {fileNames.map((n, i) => <span key={i} style={{ fontSize: 11, color: '#065f46', fontFamily: F.mono }}>{n}</span>)}
                  </div>
                )}
                <input type="file" accept=".csv" multiple style={{ display: 'none' }} onChange={e => handleFiles(e.target.files)} />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button style={btn('secondary')} onClick={onClose}>Cancel</button>
              <button style={btn('primary')} onClick={doPreview} disabled={!csvTexts.length}>
                {parsing ? 'Parsing…' : 'Preview import'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ padding: '10px 12px', background: C.fa, borderRadius: 8, border: `1px solid ${C.e8}`, marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: C.text }}>{preview.length} products found</div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                {preview.filter(p => existingProducts.some(e => (p.handle && e.handle === p.handle) || (p.skuBase && e.skuBase === p.skuBase))).length} updates · {preview.filter(p => !existingProducts.some(e => (p.handle && e.handle === p.handle) || (p.skuBase && e.skuBase === p.skuBase))).length} new
                {remoteImageCount > 0 && ` · ${remoteImageCount} image${remoteImageCount === 1 ? '' : 's'} to import`}
              </div>
            </div>
            <div style={{ maxHeight: 320, overflowY: 'auto', marginBottom: 16 }}>
              {preview.map((p, i) => {
                const isUpdate = existingProducts.some(e => (p.handle && e.handle === p.handle) || (p.skuBase && e.skuBase === p.skuBase));
                return (
                <div key={i} style={{ padding: '8px 0', borderBottom: `1px solid ${C.e8}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{p.title || p.handle}</div>
                    <div style={{ fontSize: 11, color: C.muted, fontFamily: F.mono }}>
                      {p.handle} · {p.variants.length} variants
                      {(p.images || []).length > 0 && ` · ${p.images.length} image${p.images.length === 1 ? '' : 's'}`}
                    </div>
                  </div>
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: isUpdate ? '#fef3c7' : '#d1fae5', color: isUpdate ? '#92400e' : '#065f46' }}>
                    {isUpdate ? 'Update' : 'New'}
                  </span>
                </div>
                );
              })}
            </div>
            {imgBlocked && (
              <div style={{ marginBottom: 12, padding: '10px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  {remoteImageCount.toLocaleString()} images is above the {MAX_IMAGES_PER_IMPORT.toLocaleString()}-per-import limit.
                </div>
                Split the sheet and import it in parts, or continue without downloading — the
                products come in either way, but their images keep pointing at the original
                links and will break if those expire.
              </div>
            )}
            {imgErrors.length > 0 && (
              <div style={{ marginBottom: 12, padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12, color: '#b91c1c' }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  {imgErrors.length} image{imgErrors.length === 1 ? '' : 's'} couldn’t be downloaded — importing anyway keeps the original link.
                </div>
                {imgErrors.slice(0, 3).map((err, i) => (
                  <div key={i} style={{ fontFamily: F.mono, fontSize: 11, wordBreak: 'break-all' }}>{err.url} — {err.message}</div>
                ))}
                {imgErrors.length > 3 && <div style={{ fontSize: 11 }}>+{imgErrors.length - 3} more</div>}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
              {imgProg && (
                <span style={{ fontSize: 12, color: C.muted, marginRight: 'auto' }}>
                  Importing images {imgProg.current}/{imgProg.total}… (already-stored links are reused, not re-fetched)
                </span>
              )}
              <button style={btn('secondary')} onClick={() => { setPreview(null); setImgBlocked(false); setImgErrors([]); }} disabled={!!imgProg}>Back</button>
              <button style={{ ...btn('primary'), opacity: imgProg ? 0.5 : 1 }} onClick={confirmImport} disabled={!!imgProg}>
                {imgProg ? 'Importing…'
                  : imgBlocked ? 'Import without downloading'
                  : imgErrors.length ? 'Import anyway'
                  : `Import ${preview.length} products`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DUPLICATE COLLECTION MODAL
// ─────────────────────────────────────────────────────────────

export function DuplicateModal({ collection, onDuplicate, onClose }) {
  const { deptIds, territories } = useTenantConfig();
  const [name, setName] = useState(`${collection.name} (copy)`);
  const [opts, setOpts] = useState({ clearPrices: false, clearBarcodes: false, clearImages: false, clearFitFabric: false });
  const toggle = k => setOpts(prev => ({ ...prev, [k]: !prev[k] }));

  const doDuplicate = () => {
    const products = collection.products.map(p => {
      const np = JSON.parse(JSON.stringify(p));
      np.id          = uid();
      np.createdAt   = new Date().toISOString();
      np.updatedAt   = new Date().toISOString();
      np.sectionStatus = Object.fromEntries(deptIds.map(d => [d, 'empty']));
      np.sectionNotes  = Object.fromEntries(deptIds.map(d => [d, '']));
      if (opts.clearPrices)    { np.basePrice = ''; np.territoryPrices = Object.fromEntries(territories.map(t => [t.code, ''])); }
      if (opts.clearBarcodes)  np.variants = np.variants.map(v => ({ ...v, barcode: '' }));
      if (opts.clearImages)    { np.imageUrl = ''; np.additionalImages = ''; }
      if (opts.clearFitFabric) np.fitFabric = '';
      return np;
    });
    onDuplicate({ id: uid(), name, products });
    onClose();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: C.white, borderRadius: 12, padding: 24, width: 440, boxShadow: '0 24px 64px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ fontFamily: F.display, fontSize: 20, color: C.text }}>Duplicate Collection</div>
          <button style={btn('ghost', { fontSize: 20, padding: '2px 8px', color: C.muted })} onClick={onClose}>×</button>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={lbl()}>New collection name</label>
          <input style={inp()} value={name} onChange={e => setName(e.target.value)} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: C.muted, marginBottom: 10 }}>Reset options</div>
          {[
            ['clearPrices',    'Clear prices (Trading re-enters)'],
            ['clearBarcodes',  'Clear barcodes (Ops re-enters)'],
            ['clearImages',    'Clear images'],
            ['clearFitFabric', 'Clear fit & fabric'],
          ].map(([k, label]) => (
            <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 8 }}>
              <input type="checkbox" checked={opts[k]} onChange={() => toggle(k)} style={{ width: 14, height: 14 }} />
              <span style={{ fontSize: 13, color: C.text }}>{label}</span>
            </label>
          ))}
          <div style={{ fontSize: 11, color: C.muted, marginTop: 10, lineHeight: 1.5 }}>
            Titles, tags, metafields, and variant structure always carry over. All approval statuses reset to empty.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button style={btn('secondary')} onClick={onClose}>Cancel</button>
          <button style={btn('primary')} onClick={doDuplicate}>Duplicate</button>
        </div>
      </div>
    </div>
  );
}
